/**
 * AnomalyRail — horizontal strip of detected anomalies for the active event.
 *
 * V3 Page 01 (2026-04-22). Fetches `/api/dashboard/:eventId/anomalies` when
 * VITE_DASHBOARD_ANOMALIES=1 + DASHBOARD_ANOMALIES=1 are both set. Renders
 * nothing on 503 (flag off server-side) so the host dashboard can keep its
 * V2.6 layout intact. localStorage persists dismissed anomaly hashes per
 * event so a refresh doesn't re-surface the same chip.
 */

import { useEffect, useMemo, useState } from 'react'
import type { Anomaly } from './AnomalyChip'
import { AnomalyChip } from './AnomalyChip'

const STORAGE_KEY = 'butler-dashboard-dismissed-anomalies'

function anomalyHash(a: Anomaly): string {
  return [
    a.kind,
    a.hour || '',
    a.attraction_id ?? '',
    a.value_cents,
  ].join('|')
}

interface DismissedMap {
  [eventId: string]: string[]
}

function readDismissed(): DismissedMap {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

function writeDismissed(next: DismissedMap) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
  } catch {
    // quota / privacy mode — failing silently means the chip returns on
    // next visit. Acceptable for a non-critical UX nicety.
  }
}

export interface AnomalyRailProps {
  eventId: number | null
  authToken?: string | null
  onExplain?: (anomaly: Anomaly) => void
}

type FetchState =
  | { phase: 'idle' }
  | { phase: 'loading' }
  | { phase: 'loaded'; anomalies: Anomaly[] }
  | { phase: 'hidden' }           // 503 — flag off or no data

export function AnomalyRail({ eventId, authToken, onExplain }: AnomalyRailProps) {
  const [state, setState] = useState<FetchState>({ phase: 'idle' })
  const [dismissed, setDismissed] = useState<string[]>([])

  useEffect(() => {
    if (eventId == null) {
      setState({ phase: 'idle' })
      setDismissed([])
      return
    }
    // Re-hydrate dismissed list on every event switch so stale hashes
    // from a previous event don't leak.
    const map = readDismissed()
    setDismissed(map[String(eventId)] || [])

    const controller = new AbortController()
    let cancelled = false
    setState({ phase: 'loading' })
    const headers: Record<string, string> = {}
    if (authToken) headers.Authorization = `Bearer ${authToken}`
    fetch(`/api/dashboard/${eventId}/anomalies`, {
      headers,
      credentials: 'same-origin',
      signal: controller.signal,
    })
      .then(async (res) => {
        if (res.status === 503) {
          if (!cancelled) setState({ phase: 'hidden' })
          return null
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return (await res.json()) as { anomalies?: Anomaly[] }
      })
      .then((data) => {
        if (cancelled || !data) return
        const anomalies = Array.isArray(data.anomalies) ? data.anomalies : []
        setState({ phase: 'loaded', anomalies })
      })
      .catch((err) => {
        if (cancelled) return
        if (err && (err as { name?: string }).name === 'AbortError') return
        // Non-critical — hide the rail quietly on error per spec.
        setState({ phase: 'hidden' })
      })
    return () => {
      cancelled = true
      controller.abort()
    }
  }, [eventId, authToken])

  const visible = useMemo(() => {
    if (state.phase !== 'loaded') return []
    const dismissedSet = new Set(dismissed)
    return state.anomalies.filter((a) => !dismissedSet.has(anomalyHash(a)))
  }, [state, dismissed])

  if (eventId == null || state.phase === 'hidden') return null
  if (state.phase === 'loading') {
    return (
      <div
        data-testid="anomaly-rail-loading"
        className="mt-4 flex gap-2 overflow-x-auto"
        aria-busy="true"
        aria-label="Scanning for anomalies"
      >
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="h-7 w-40 animate-pulse rounded-full bg-hairline dark:bg-slate-700"
          />
        ))}
      </div>
    )
  }
  if (state.phase === 'loaded' && visible.length === 0) {
    return (
      <div
        data-testid="anomaly-rail-empty"
        className="mt-4 text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-soft dark:text-slate-400"
      >
        No anomalies flagged · scanning live
      </div>
    )
  }
  return (
    <div
      data-testid="anomaly-rail"
      className="mt-4 flex flex-wrap items-center gap-2"
      role="region"
      aria-label="Detected anomalies"
    >
      <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-ink-soft dark:text-slate-400">
        Watch signals
      </span>
      {visible.map((a) => (
        <AnomalyChip
          key={anomalyHash(a)}
          anomaly={a}
          onExplain={onExplain}
          onDismiss={(anomaly) => {
            const hash = anomalyHash(anomaly)
            const next = [...dismissed, hash]
            setDismissed(next)
            if (eventId != null) {
              const map = readDismissed()
              map[String(eventId)] = next
              writeDismissed(map)
            }
          }}
        />
      ))}
    </div>
  )
}
